import styled, { css } from 'styled-components';

// Media query breakpoints
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px'
};

// Media query helper
const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

// Base Container component
export const Container = styled.div`
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing.md};
  min-height: ${props => props.fullHeight ? '100vh' : 'auto'};
  font-family: ${props => props.theme.typography.fontFamily};
  width: 100%;
  max-width: ${props => props.fluid ? '100%' : '1400px'};
  margin: 0 auto;
  
  ${media.md`
    padding: ${props => props.theme.spacing.xl};
  `}

  ${({ variant, theme }) => variant === 'centered' && css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
  `}

  ${({ variant, theme }) => variant === 'narrow' && css`
    max-width: 800px;
  `}

  ${({ variant, theme }) => variant === 'wide' && css`
    max-width: 1600px;
  `}

  ${({ noPadding }) => noPadding && css`
    padding: 0;
  `}
`;

// Login specific container
export const LoginContainer = styled(Container)`
  display: flex;
  flex-direction: column; 
  justify-content: center;
  align-items: center;
  min-height: 100vh; 
  padding: ${({ theme }) => theme.spacing.md}; 
  background-color: ${({ theme }) => theme.colors.background}; 
  position: relative;
`;

// Dashboard container
export const DashboardContainer = styled(Container)`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
  
  ${media.md`
    grid-template-columns: 250px 1fr;
    gap: ${props => props.theme.spacing.xl};
  `}
  
  ${media.lg`
    grid-template-columns: 300px 1fr;
  `}
`;

// Main content area
export const MainContent = styled.main`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.fontFamily};
  background-color: ${props => props.backgroundColor || props.theme.colors.background};
  
  ${media.md`
    padding: ${props => props.theme.spacing.lg};
  `}
`;

// Sidebar container
export const Sidebar = styled.aside`
  background-color: ${props => props.theme.colors.backgroundAlt || props.theme.colors.background};
  padding: ${props => props.theme.spacing.md};
  border-right: 1px solid ${props => props.theme.colors.border};
  font-family: ${props => props.theme.typography.fontFamily};
  
  ${media.md`
    padding: ${props => props.theme.spacing.lg};
  `}

  ${({ position }) => position === 'right' && css`
    border-right: none;
    border-left: 1px solid ${props => props.theme.colors.border};
  `}
`;

// Header container
export const Header = styled.header`
  background-color: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.fontFamily};
  position: ${props => props.sticky ? 'sticky' : 'static'};
  top: 0;
  z-index: 100;
  
  ${media.md`
    padding: ${props => props.theme.spacing.lg};
  `}

  ${({ variant, theme }) => variant === 'large' && css`
    padding: ${theme.spacing.lg} ${theme.spacing.md};
    
    ${media.md`
      padding: ${theme.spacing.xl} ${theme.spacing.lg};
    `}
  `}
`;

// Footer container
export const Footer = styled.footer`
  background-color: ${props => props.theme.colors.backgroundAlt || props.theme.colors.background};
  border-top: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.md};
  margin-top: auto;
  font-family: ${props => props.theme.typography.fontFamily};
  text-align: ${props => props.align || 'left'};
  
  ${media.md`
    padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  `}
`;

// Section containers
export const Section = styled.section`
  padding: ${props => props.theme.spacing.lg} 0;
  font-family: ${props => props.theme.typography.fontFamily};
  background-color: ${props => props.backgroundColor || 'transparent'};
  
  ${media.md`
    padding: ${props => props.theme.spacing.xl} 0;
  `}
  
  ${media.lg`
    padding: ${props => props.theme.spacing.xxl} 0;
  `}

  ${({ variant, theme }) => variant === 'hero' && css`
    padding: ${theme.spacing.xxl} 0;
    text-align: center;
    background: linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20);
    
    ${media.md`
      padding: calc(${theme.spacing.xxl} * 1.5) 0;
    `}
  `}

  ${({ variant, theme }) => variant === 'compact' && css`
    padding: ${theme.spacing.md} 0;
    
    ${media.md`
      padding: ${theme.spacing.lg} 0;
    `}
  `}
`;

// Content wrapper
export const ContentWrapper = styled.div`
  max-width: ${props => {
    switch (props.size) {
      case 'sm': return '640px';
      case 'md': return '768px';
      case 'lg': return '1024px';
      case 'xl': return '1280px';
      case 'full': return '100%';
      default: return '1024px';
    }
  }};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.fontFamily};
  
  ${media.md`
    padding: 0 ${props => props.theme.spacing.lg};
  `}
`;

// Layout components
export const FlexContainer = styled.div`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  flex-wrap: ${props => props.wrap || 'wrap'};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'stretch'};
  gap: ${props => props.gap ? props.theme.spacing[props.gap] : props.theme.spacing.md};
  
  ${props => props.responsive && css`
    flex-direction: column;
    
    ${media.md`
      flex-direction: ${props => props.direction || 'row'};
    `}
  `}

  ${({ fullHeight }) => fullHeight && css`
    min-height: 100vh;
  `}
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${props => props.mobileMinWidth || '100%'}, 1fr));
  gap: ${props => props.theme.spacing.md};
  
  ${media.sm`
    grid-template-columns: repeat(auto-fit, minmax(${props => props.minWidth || '250px'}, 1fr));
    gap: ${props => props.theme.spacing.lg};
  `}

  ${({ columns }) => columns && css`
    grid-template-columns: repeat(${columns}, 1fr);
  `}

  ${({ responsive, columns }) => responsive && columns && css`
    grid-template-columns: 1fr;
    
    ${media.sm`
      grid-template-columns: repeat(${Math.min(columns, 2)}, 1fr);
    `}
    
    ${media.md`
      grid-template-columns: repeat(${Math.min(columns, 3)}, 1fr);
    `}
    
    ${media.lg`
      grid-template-columns: repeat(${columns}, 1fr);
    `}
  `}
`;

// Specialized containers
export const CardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
  
  ${media.sm`
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  `}
  
  ${media.lg`
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: ${props => props.theme.spacing.xl};
  `}
`;

export const FormContainer = styled.div`
  max-width: ${props => props.maxWidth || '600px'};
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.elevated ? props.theme.shadows.medium : 'none'};
  font-family: ${props => props.theme.typography.fontFamily};
  
  ${media.md`
    padding: ${props => props.theme.spacing.xl};
  `}
`;

export const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.small};
  margin: ${props => props.theme.spacing.lg} 0;
`;

// Utility containers
export const Spacer = styled.div`
  width: ${props => props.width || 'auto'};
  height: ${props => props.height || 'auto'};
  flex: ${props => props.flex || 'none'};
`;

export const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${props => props.theme.colors.border};
  margin: ${props => props.theme.spacing.lg} 0;
  
  ${({ orientation }) => orientation === 'vertical' && css`
    width: 1px;
    height: auto;
    margin: 0 ${props => props.theme.spacing.lg};
  `}

  ${({ variant, theme }) => variant === 'thick' && css`
    height: 2px;
    background-color: ${theme.colors.primary};
  `}

  ${({ variant, theme }) => variant === 'dashed' && css`
    border-top: 1px dashed ${theme.colors.border};
    background: none;
    height: 0;
  `}
`;

// Modal/Overlay containers
export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, ${props => props.opacity || 0.5});
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.md};
  backdrop-filter: ${props => props.blur ? 'blur(4px)' : 'none'};
`;

export const Modal = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.md};
  max-width: ${props => props.maxWidth || '500px'};
  width: 100%;
  max-height: 90vh;
  // overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xlarge || props.theme.shadows.large};
  position: relative;
  font-family: ${props => props.theme.typography.fontFamily};
  
  ${({ size, theme }) => size === 'small' && css`
    max-width: 400px;
    padding: ${theme.spacing.lg};
  `}

  ${({ size, theme }) => size === 'large' && css`
    max-width: 800px;
    padding: ${theme.spacing.xxl};
  `}

  ${({ size }) => size === 'fullscreen' && css`
    max-width: 95vw;
    max-height: 95vh;
    width: 95vw;
    height: 95vh;
  `}
`;

// Scrollable container
export const ScrollContainer = styled.div`
  overflow-y: auto;
  max-height: ${props => props.maxHeight || '400px'};
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.medium};
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.borderLight};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

// Page layout containers
export const PageLayout = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: ${props => props.theme.typography.fontFamily};
`;

export const PageHeader = styled(Header)`
  flex-shrink: 0;
`;

export const PageMain = styled(MainContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const PageFooter = styled(Footer)`
  flex-shrink: 0;
`;

// Responsive container
export const ResponsiveContainer = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  
  ${media.sm`
    padding: ${props => props.theme.spacing.md};
  `}
  
  ${media.md`
    padding: ${props => props.theme.spacing.lg};
  `}
  
  ${media.lg`
    padding: ${props => props.theme.spacing.xl};
  `}
`;

// Animated container
export const AnimatedContainer = styled.div`
  transition: all 0.3s ease;
  
  ${({ fadeIn }) => fadeIn && css`
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s ease forwards;
    
    @keyframes fadeInUp {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}

  ${({ slideIn }) => slideIn && css`
    transform: translateX(-100%);
    animation: slideIn 0.4s ease forwards;
    
    @keyframes slideIn {
      to {
        transform: translateX(0);
      }
    }
  `}
`;

// Floating logo component (moved from ThemeComponents)
export const FloatingLogo = styled.img`
  position: fixed;
  bottom: -80px;
  right: 20px;
  height: 20px; // Changed from 300px to 200px
  width: auto;
  z-index: 10;
  opacity: 0.7;
  
  ${media.md`
    height: 40px;
    right: 30px;
    bottom: 15px;
  `}
`;